import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { createCategory } from "@/apis/material-request-category";
import { enqueueSnackbar } from "notistack";

interface CategoryOption {
  value: string;
  label: string;
}

interface FormData {
  categoryName: string;
  categoryDesc: string;
  templateId: number;
  categoryLevel: number;
  parentMasterCategoryId: number;
  dispOrder: number;
  demoPageUrl: string;
  anchorId: string;
  locationDesc?: string;
  screenshotUrl?: string;
}

interface UseCreateProps {
  onClose: () => void;
  comId: string;
  onSuccess?: () => void;
}

export default function useCreate({
  onClose,
  comId,
  onSuccess,
}: UseCreateProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [requestType, setRequestType] = useState<number>(1); // 1: 페이지, 2: 섹션
  const [selectedPageId, setSelectedPageId] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // API에서 가져와야 하지만, 임시로 하드코딩한 페이지 목록
  const pages: CategoryOption[] = [
    { value: "340", label: "About 페이지" },
    { value: "341", label: "Schedule 페이지" },
    { value: "342", label: "Gallery 페이지" },
    { value: "343", label: "Contact 페이지" },
  ];

  // 폼 초기화
  const { control, handleSubmit, reset, setValue } = useForm<FormData>({
    defaultValues: {
      categoryName: "",
      categoryDesc: "",
      templateId: 1,
      categoryLevel: requestType === 1 ? 1 : 2,
      parentMasterCategoryId:
        requestType === 2 ? parseInt(selectedPageId || "0") : 1,
      dispOrder: 0,
      demoPageUrl: "https://example.com/demo/template1",
      anchorId: requestType === 2 ? "section1" : "",
      locationDesc: "",
      screenshotUrl: "",
    },
  });

  // requestType 변경 시 categoryLevel 및 parentMasterCategoryId 업데이트
  useEffect(() => {
    setValue("categoryLevel", requestType === 1 ? 1 : 2);
    setValue(
      "parentMasterCategoryId",
      requestType === 2 ? parseInt(selectedPageId || "0") : 1
    );
    setValue("anchorId", requestType === 2 ? "section1" : "");
  }, [requestType, selectedPageId, setValue]);

  // 페이지 선택 변경 핸들러
  const handlePageChange = (pageId: string) => {
    setSelectedPageId(pageId);
    setValue("parentMasterCategoryId", parseInt(pageId || "0"));
  };

  // 페이지 강제 새로고침 핸들러
  const handleRefreshPages = () => {
    setIsRefreshing(true);
    // 실제로는 여기서 API를 호출하여 페이지 목록을 새로 가져와야 합니다
    setTimeout(() => {
      setIsRefreshing(false);
      enqueueSnackbar("페이지 목록이 새로고침되었습니다.", {
        variant: "success",
      });
    }, 1000);
  };

  // 폼 제출 핸들러 - 페이지 요청용 (모달 닫지 않음)
  const onSubmitPage = async (data: FormData) => {
    if (!comId) {
      enqueueSnackbar("회사 ID가 필요합니다.", { variant: "error" });
      return;
    }

    setIsLoading(true);
    try {
      const requestData = {
        comId: Number(comId),
        categoryName: data.categoryName,
        categoryLevel: 1, // 페이지는 항상 Level 1
        categoryDesc: data.categoryDesc,
        dispOrder: data.dispOrder,
        demoPageUrl: data.demoPageUrl,
        anchorId: data.anchorId,
        locationDesc: data.locationDesc,
        screenshotUrl: data.screenshotUrl,
        createdBy: "20", // 실제 구현에서는 로그인된 관리자 ID를 사용
      };

      const response = await createCategory(requestData);

      if (response.success) {
        enqueueSnackbar("페이지 요청이 성공적으로 등록되었습니다.", {
          variant: "success",
        });
        reset(); // 폼 초기화만 수행하고 모달은 유지
        if (onSuccess) onSuccess(); // 성공 콜백
      } else {
        enqueueSnackbar(response.message || "요청 등록에 실패했습니다.", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("요청 등록 중 오류 발생:", error);
      enqueueSnackbar("요청 등록 중 오류가 발생했습니다.", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 폼 제출 핸들러 - 섹션 요청용 (모달 닫힘)
  const onSubmitSection = async (data: FormData) => {
    if (!comId) {
      enqueueSnackbar("회사 ID가 필요합니다.", { variant: "error" });
      return;
    }

    // 필수 값 검증
    if (!selectedPageId) {
      enqueueSnackbar("페이지를 선택해주세요.", { variant: "error" });
      return;
    }

    setIsLoading(true);
    try {
      const requestData = {
        comId: Number(comId),
        categoryName: data.categoryName,
        categoryLevel: 2, // 섹션은 항상 Level 2
        parentMasterCategoryId: parseInt(selectedPageId),
        categoryDesc: data.categoryDesc,
        dispOrder: data.dispOrder,
        demoPageUrl: data.demoPageUrl,
        anchorId: data.anchorId,
        locationDesc: data.locationDesc,
        screenshotUrl: data.screenshotUrl,
        createdBy: "20", // 실제 구현에서는 로그인된 관리자 ID를 사용
      };

      const response = await createCategory(requestData);

      if (response.success) {
        enqueueSnackbar("섹션 요청이 성공적으로 등록되었습니다.", {
          variant: "success",
        });
        reset(); // 폼 초기화
        onClose(); // 모달 닫기
        if (onSuccess) onSuccess(); // 성공 콜백
      } else {
        enqueueSnackbar(response.message || "요청 등록에 실패했습니다.", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("요청 등록 중 오류 발생:", error);
      enqueueSnackbar("요청 등록 중 오류가 발생했습니다.", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 모달이 닫힐 때 상태 초기화
  const handleClose = () => {
    reset();
    setRequestType(1);
    setSelectedPageId("");
    onClose();
  };

  return {
    isLoading,
    isRefreshing,
    requestType,
    selectedPageId,
    pages,
    control,
    handleSubmit,
    onSubmitPage,
    onSubmitSection,
    handleClose,
    setRequestType,
    handlePageChange,
    handleRefreshPages,
  };
}
